import { ChevronRight } from 'lucide-react'
import { cn, formatDateHuman, formatRelative } from '@/lib/utils'
import { T, kindLabel } from '@/lib/inventoryFlow/strings'
import type { Requisition } from '@/lib/inventoryFlow/types'
import { StatusChip } from './StatusChip'
import { todoChip, todoTitle, type ChipTone } from '@/lib/inventoryFlow/storeScreenLogic'
import { ST } from '@/lib/inventoryFlow/storeScreenStrings'
import type { RequisitionStreamStatus } from '@/lib/inventoryFlow/requisitionStream'

/** Big-row list of requisitions with status chip and fill %. */
export function RequisitionList({
  items,
  selectedId,
  onSelect,
  emptyText = T.empty,
  missingLabel = T.missing,
}: {
  items: Requisition[]
  selectedId?: string | null
  onSelect: (id: string) => void
  emptyText?: string
  missingLabel?: string
}) {
  if (items.length === 0) return <p className="p-4 text-muted-foreground">{emptyText}</p>
  return (
    <ul className="divide-y rounded-lg border">
      {items.map((r) => (
        <li key={r.id}>
          <button type="button" onClick={() => onSelect(r.id)}
            className={cn('flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-muted/50 touch-manipulation', selectedId === r.id && 'bg-muted')}>
            <span className="min-w-0 flex-1 space-y-1">
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{r.req_no}</span>
                <StatusChip status={r.status} kind={r.kind} />
                {r.has_shortage && <span className="text-xs font-semibold text-red-700 dark:text-red-300">{missingLabel}</span>}
              </span>
              <span className="block text-sm text-muted-foreground">
                {kindLabel(r.kind)} · {r.line_count} item{r.line_count === 1 ? '' : 's'} · {r.from_location.name} → {r.to_location.name}
                {r.requested_by_name ? ` · ${r.requested_by_name}` : ''} · {formatDateHuman(r.created_at)}
              </span>
              {r.status !== 'draft' && (
                <span className="block text-xs text-muted-foreground">{T.sent} {r.fill.issued_pct}% · {T.got} {r.fill.received_pct}%</span>
              )}
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </li>
      ))}
    </ul>
  )
}

/** The dot in a tab or header that says whether live updates are flowing. */
export function LiveDot({ status }: { status: RequisitionStreamStatus }) {
  const live = status === 'live'
  const reconnecting = status === 'reconnecting'
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" role="status">
      <span className={cn('h-2 w-2 rounded-full', live ? 'bg-emerald-500' : reconnecting ? 'bg-amber-500' : 'bg-muted-foreground/50')} />
      {live ? T.live : reconnecting ? T.reconnecting : T.offline}
    </span>
  )
}

const CHIP_TONE: Record<ChipTone, string> = {
  new: 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200',
  wait: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200',
  move: 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200',
  bad: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200',
  good: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200',
  neutral: 'bg-muted text-muted-foreground',
}

/** The store's To do (batch 4): each row says when it is needed, which event
 *  it is for, how many items, who asked, and what the store must do next. */
export function StoreTodoList({
  items,
  selectedId,
  onSelect,
  isAdmin,
  emptyText,
  header,
}: {
  items: Requisition[]
  selectedId?: string | null
  onSelect: (id: string) => void
  isAdmin: boolean
  emptyText: string
  header?: string
}) {
  if (items.length === 0) return <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">{emptyText}</p>
  return (
    <div className="overflow-hidden rounded-lg border">
      {header && <div className="border-b px-3 py-2 text-xs text-muted-foreground">{header}</div>}
      <ul className="divide-y">
        {items.map((r) => {
          const title = todoTitle(r)
          const chip = todoChip(r, isAdmin)
          const shortage = r.has_shortage && chip.tone === 'bad'
          return (
            <li key={r.id}>
              <button type="button" onClick={() => onSelect(r.id)} aria-current={selectedId === r.id || undefined}
                className={cn('block w-full px-3 py-3 text-left transition-colors hover:bg-muted/50', selectedId === r.id && 'bg-muted')}>
                <span className="flex items-start justify-between gap-2">
                  <span className={cn('font-semibold', title.tone === 'overdue' && 'text-red-700 dark:text-red-300')}>{title.text}</span>
                  <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-xs font-medium', CHIP_TONE[chip.tone])}>{chip.label}</span>
                </span>
                <span className="mt-0.5 block text-sm text-muted-foreground">
                  {r.production_plan_title || kindLabel(r.kind)} · {ST.todo.items(r.line_count)}
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {[r.requested_by_name ? ST.todo.from(r.requested_by_name) : null, formatRelative(r.submitted_at ?? r.created_at), r.req_no]
                    .filter(Boolean).join(' · ')}
                </span>
                {shortage && <span className="mt-0.5 block text-xs text-muted-foreground">{ST.todo.missingHint}</span>}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
